/**
 * OneSignal Notification Service
 * Uses OneSignal Web SDK for push notifications
 */

import { supabase } from '@/integrations/supabase/client';

// ============= TYPES =============

export interface OneSignalNotificationStatus {
  isSupported: boolean;
  isInitialized: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  userId: string | null;
  isIOS: boolean;
  isInstalledPWA: boolean;
}

export interface OneSignalResult {
  success: boolean;
  error?: string;
}

// ============= PLATFORM DETECTION =============

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isInstalledPWA(): boolean {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = (navigator as { standalone?: boolean }).standalone === true;
  return isStandalone || isIOSStandalone;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
}

export function getPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

// ============= ONESIGNAL SDK =============

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: OneSignalInstance) => void | Promise<void>>;
    OneSignal?: OneSignalInstance;
  }
}

interface OneSignalInstance {
  init: (config: OneSignalConfig) => Promise<void>;
  login: (externalId: string) => Promise<void>;
  logout: () => Promise<void>;
  Notifications: {
    permission: boolean;
    permissionNative: NotificationPermission;
    requestPermission: () => Promise<void>;
    addEventListener: (event: string, callback: (data: unknown) => void) => void;
    removeEventListener: (event: string, callback: (data: unknown) => void) => void;
  };
  User: {
    PushSubscription: {
      optIn: () => Promise<void>;
      optOut: () => Promise<void>;
      optedIn: boolean;
      id: string | null;
    };
  };
}

interface OneSignalConfig {
  appId: string;
  allowLocalhostAsSecureOrigin?: boolean;
  autoResubscribe?: boolean;
  notifyButton?: {
    enable: boolean;
  };
  promptOptions?: {
    slidedown: {
      prompts: Array<{
        type: string;
        autoPrompt: boolean;
        text: {
          actionMessage: string;
          acceptButton: string;
          cancelButton: string;
        };
        delay?: {
          pageViews?: number;
          timeDelay?: number;
        };
      }>;
    };
  };
  serviceWorkerParam?: {
    scope: string;
  };
  serviceWorkerPath?: string;
}

// App ID (public key, safe to use in client)
const ONESIGNAL_APP_ID = '58e9c2ca-2dd1-448b-9bb0-1b9497a88293';

let isInitialized = false;
let initPromise: Promise<void> | null = null;
let initStartTime: number | null = null;
let initError: string | null = null;

// Timeout for initialization (15 seconds for iOS, 10 for others)
const INIT_TIMEOUT_MS = isIOS() ? 15000 : 10000;

/**
 * Get initialization diagnostics
 */
export function getInitDiagnostics(): { startTime: number | null; error: string | null; duration: number | null } {
  return {
    startTime: initStartTime,
    error: initError,
    duration: initStartTime ? Date.now() - initStartTime : null,
  };
}

/**
 * Initialize OneSignal SDK with timeout protection
 */
export async function initOneSignal(): Promise<boolean> {
  if (isInitialized) {
    console.log('[OneSignal] Already initialized');
    return true;
  }

  if (initPromise) {
    await initPromise;
    return isInitialized;
  }

  if (!ONESIGNAL_APP_ID) {
    initError = 'App ID not configured';
    console.error('[OneSignal] App ID not configured');
    return false;
  }

  initStartTime = Date.now();
  initError = null;
  console.log('[OneSignal] Initializing with app ID:', ONESIGNAL_APP_ID.substring(0, 8) + '...');

  initPromise = new Promise<void>((resolve) => {
    // Timeout protection
    const timeoutId = setTimeout(() => {
      if (!isInitialized) {
        initError = `Timeout after ${INIT_TIMEOUT_MS}ms`;
        console.error('[OneSignal] Initialization timeout - may be iOS/Safari issue');
        resolve();
      }
    }, INIT_TIMEOUT_MS);

    // Load OneSignal SDK script
    if (!window.OneSignalDeferred) {
      window.OneSignalDeferred = [];
      
      const script = document.createElement('script');
      script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
      script.defer = true;
      
      script.onerror = () => {
        clearTimeout(timeoutId);
        initError = 'Failed to load OneSignal SDK script';
        console.error('[OneSignal] Failed to load SDK script');
        resolve();
      };
      
      document.head.appendChild(script);
    }

    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.init({
          appId: ONESIGNAL_APP_ID,
          allowLocalhostAsSecureOrigin: true,
          autoResubscribe: true,
          notifyButton: {
            enable: false,
          },
          promptOptions: {
            slidedown: {
              prompts: [{
                type: 'push',
                autoPrompt: true,
                text: {
                  actionMessage: 'Recevez des notifications pour ne manquer aucun message.',
                  acceptButton: 'Autoriser',
                  cancelButton: 'Plus tard',
                },
                delay: {
                  pageViews: 1,
                  timeDelay: 3,
                },
              }],
            },
          },
          serviceWorkerParam: { scope: '/' },
          serviceWorkerPath: '/OneSignalSDKWorker.js',
        });
        
        clearTimeout(timeoutId);
        const duration = Date.now() - (initStartTime || 0);
        console.log(`[OneSignal] ✓ SDK initialized in ${duration}ms`);
        isInitialized = true;
        initError = null;
        resolve();
      } catch (error) {
        clearTimeout(timeoutId);
        const errorMsg = error instanceof Error ? error.message : String(error);
        initError = errorMsg;
        console.error('[OneSignal] Init error:', errorMsg);
        
        // iOS-specific error handling
        if (isIOS()) {
          console.warn('[OneSignal] iOS detected - checking for common issues...');
          if (!isInstalledPWA()) {
            initError = 'iOS requires PWA installation';
            console.warn('[OneSignal] App must be installed as PWA on iOS');
          }
        }
        
        resolve();
      }
    });
  });

  await initPromise;
  return isInitialized;
}

/**
 * Get OneSignal instance (wait for init if needed)
 */
async function getOneSignal(): Promise<OneSignalInstance | null> {
  await initOneSignal();
  return window.OneSignal || null;
}

/**
 * Login user with their Supabase user ID as external_id
 */
export async function loginUser(userId: string): Promise<boolean> {
  try {
    const OneSignal = await getOneSignal();
    if (!OneSignal) {
      console.error('[OneSignal] SDK not available');
      return false;
    }

    console.log('[OneSignal] Logging in user:', userId.substring(0, 8) + '...');
    await OneSignal.login(userId);
    console.log('[OneSignal] ✓ User logged in');
    return true;
  } catch (error) {
    console.error('[OneSignal] Login error:', error);
    return false;
  }
}

/**
 * Logout user from OneSignal
 */
export async function logoutUser(): Promise<void> {
  try {
    const OneSignal = await getOneSignal();
    if (OneSignal) {
      await OneSignal.logout();
      console.log('[OneSignal] ✓ User logged out');
    }
  } catch (error) {
    console.error('[OneSignal] Logout error:', error);
  }
}

/**
 * Request notification permission via OneSignal
 */
export async function requestPermission(): Promise<boolean> {
  try {
    const OneSignal = await getOneSignal();
    if (!OneSignal) {
      console.error('[OneSignal] SDK not available');
      return false;
    }

    console.log('[OneSignal] Requesting permission...');
    await OneSignal.Notifications.requestPermission();
    
    const granted = OneSignal.Notifications.permission;
    console.log('[OneSignal] Permission granted:', granted);
    return granted;
  } catch (error) {
    console.error('[OneSignal] Permission error:', error);
    return false;
  }
}

/**
 * Subscribe user to push notifications with retry logic
 */
export async function subscribe(userId: string, retryCount = 0): Promise<OneSignalResult> {
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 2000;
  
  console.log('[OneSignal] Subscribe for user:', userId.substring(0, 8) + '...', retryCount > 0 ? `(retry ${retryCount})` : '');

  try {
    // Initialize if needed
    if (!await initOneSignal()) {
      const errorMsg = isIOS() 
        ? 'Initialisation échouée. Vérifiez que l\'app est installée sur l\'écran d\'accueil.' 
        : 'OneSignal not initialized';
      return { success: false, error: errorMsg };
    }

    const OneSignal = await getOneSignal();
    if (!OneSignal) {
      return { success: false, error: 'OneSignal SDK not available' };
    }

    // Login with external_id first
    const loggedIn = await loginUser(userId);
    if (!loggedIn) {
      return { success: false, error: 'Failed to login user' };
    }

    // Request permission if needed
    if (!OneSignal.Notifications.permission) {
      const granted = await requestPermission();
      if (!granted) {
        // Don't update profile flag if permission failed
        return { success: false, error: 'Permission not granted' };
      }
    }

    // Opt-in to push
    if (!OneSignal.User.PushSubscription.optedIn) {
      await OneSignal.User.PushSubscription.optIn();
    }

    // Verify subscription is actually active before updating profile
    const subscriptionId = OneSignal.User.PushSubscription.id;
    if (!subscriptionId && !OneSignal.User.PushSubscription.optedIn) {
      // Subscription failed silently - retry if possible
      if (retryCount < MAX_RETRIES) {
        console.log(`[OneSignal] Subscription not confirmed, retrying in ${RETRY_DELAY_MS}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        return subscribe(userId, retryCount + 1);
      }
      return { success: false, error: 'Subscription non confirmée après plusieurs tentatives' };
    }

    // Update profile only after confirmed subscription
    await supabase
      .from('profiles')
      .update({ push_notifications_enabled: true })
      .eq('id', userId);

    console.log('[OneSignal] ✓ Subscription complete');
    return { success: true };
  } catch (error) {
    console.error('[OneSignal] Subscribe error:', error);
    
    // Retry on failure for iOS
    if (isIOS() && retryCount < MAX_RETRIES) {
      console.log(`[OneSignal] iOS error, retrying in ${RETRY_DELAY_MS}ms...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      return subscribe(userId, retryCount + 1);
    }
    
    return { success: false, error: String(error) };
  }
}

/**
 * Unsubscribe user from push notifications
 */
export async function unsubscribe(userId: string): Promise<boolean> {
  console.log('[OneSignal] Unsubscribe for user:', userId.substring(0, 8) + '...');

  try {
    const OneSignal = await getOneSignal();
    if (OneSignal && OneSignal.User.PushSubscription.optedIn) {
      await OneSignal.User.PushSubscription.optOut();
    }

    // Update profile
    await supabase
      .from('profiles')
      .update({ push_notifications_enabled: false })
      .eq('id', userId);

    console.log('[OneSignal] ✓ Unsubscribed');
    return true;
  } catch (error) {
    console.error('[OneSignal] Unsubscribe error:', error);
    return false;
  }
}

/**
 * Get current notification status
 */
export async function getStatus(): Promise<OneSignalNotificationStatus> {
  const status: OneSignalNotificationStatus = {
    isSupported: isPushSupported(),
    isInitialized: false,
    permission: getPermission(),
    isSubscribed: false,
    userId: null,
    isIOS: isIOS(),
    isInstalledPWA: isInstalledPWA(),
  };

  try {
    const OneSignal = await getOneSignal();
    if (OneSignal) {
      status.isInitialized = true;
      status.permission = OneSignal.Notifications.permissionNative || getPermission();
      status.isSubscribed = OneSignal.User.PushSubscription.optedIn || false;
      status.userId = OneSignal.User.PushSubscription.id || null;
    }
  } catch (error) {
    console.error('[OneSignal] Status error:', error);
  }

  return status;
}

/**
 * Send notification via edge function (server-side)
 */
export async function sendNotification(
  userId: string,
  title: string,
  body: string,
  data?: Record<string, unknown>
): Promise<OneSignalResult> {
  console.log('[OneSignal] Sending notification to:', userId.substring(0, 8) + '...');

  try {
    const { data: result, error } = await supabase.functions.invoke('send-push-notification', {
      body: { userId, title, body, data }
    });

    if (error) {
      console.error('[OneSignal] Edge function error:', error);
      return { success: false, error: error.message };
    }

    console.log('[OneSignal] Notification result:', result);
    return { success: result?.success || false, error: result?.error };
  } catch (error) {
    console.error('[OneSignal] Send error:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Show local test notification
 */
export async function showLocalNotification(
  title: string,
  body: string
): Promise<boolean> {
  if (Notification.permission !== 'granted') {
    console.warn('[OneSignal] Permission not granted for local notification');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      body,
      icon: '/pwa-192x192.png',
      badge: '/pwa-192x192.png',
      tag: `local-${Date.now()}`,
    });
    return true;
  } catch (error) {
    console.error('[OneSignal] Local notification error:', error);
    return false;
  }
}

/**
 * Check if user has a valid subscription in the database
 */
export async function hasDbSubscription(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('push_subscriptions')
      .select('id')
      .eq('user_id', userId)
      .limit(1);
    
    if (error) {
      console.error('[OneSignal] DB subscription check error:', error);
      return false;
    }
    
    return (data?.length ?? 0) > 0;
  } catch (error) {
    console.error('[OneSignal] DB subscription check error:', error);
    return false;
  }
}

/**
 * Repair desynchronized subscription state
 * Forces a complete re-subscription flow
 */
export async function repairSubscription(userId: string): Promise<OneSignalResult> {
  console.log('[OneSignal] Starting subscription repair for:', userId.substring(0, 8) + '...');
  
  try {
    // 1. Clear the profile flag first
    await supabase
      .from('profiles')
      .update({ push_notifications_enabled: false })
      .eq('id', userId);
    
    // 2. Delete any orphan subscriptions
    await supabase
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId);
    
    // 3. Opt out from OneSignal if possible
    const OneSignal = await getOneSignal();
    if (OneSignal && OneSignal.User.PushSubscription.optedIn) {
      try {
        await OneSignal.User.PushSubscription.optOut();
      } catch (e) {
        console.warn('[OneSignal] OptOut during repair failed:', e);
      }
    }
    
    // 4. Wait a moment for state to clear
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // 5. Re-subscribe fresh
    return await subscribe(userId);
  } catch (error) {
    console.error('[OneSignal] Repair error:', error);
    return { success: false, error: String(error) };
  }
}
